import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'
import {
	Loader2,
	Plug,
	Sparkles,
	ArrowRight,
	ArrowLeft,
	Check,
	ShieldCheck,
	CreditCard,
	Clock,
	FileText,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { useOrgSlug } from '@/hooks/use-org-slug'
import { usePrivilegedStaffBypass } from '@/hooks/use-privileged-staff-bypass'
import {
	useCompleteBillingSetup,
	useCreateBillingSetupSession,
	type BillingSetupSession,
} from '@/lib/api/billing'
import { usePaymentStatus } from '@/hooks/use-payment-status'
import {
	useProcessingApplication,
	type ProcessingApplicationState,
} from '@/hooks/use-processing-application'
import { ORG_ROUTES } from '@/config/router'
import { brandingConfig, isActivateAccountFunnelEnabled } from '@/config/branding'
import { BlockingPageAccountBar } from '@/components/layout/blocking-page-account-bar'
import { getOnboardingState } from '@/lib/onboarding'
import { getSetupPath } from '@/lib/setupFeatures'
import { withDashboardCometToken } from '@/lib/tagadapay-dashboard'
import { ONBOARDING_PROCESSOR_LOGOS } from './processor-logos'
import { EmbeddedCardSetup } from './components/EmbeddedCardSetup'
import { VaultCardSetup } from './components/VaultCardSetup'
import {
	CARD_SETUP_RETURN_ERRORS,
	currentPageReturnUrl,
	readCardSetupReturn,
} from '@/lib/billing-card-setup-return'

/**
 * Post-activation landing. Kept in ONE place because the card-setup success
 * callback and the page-level hasPaymentMethod redirect race each other —
 * both must resolve to the same destination (see comment at the redirect).
 * Guided setup (store_create intent) lands on /setup, other onboarding
 * intents continue to store creation, everyone else goes home.
 */
function resolvePostActivationDestination(urlSlug: string): string {
	const intent = getOnboardingState()?.intent
	if (intent?.type === 'store_create') {
		return getSetupPath(urlSlug)
	}
	return intent ? `/org/${urlSlug}/stores/create` : ORG_ROUTES(urlSlug).BASE()
}

/** TagadaPay dashboard (Merchant-of-Record application lives there, not here). */
const DASHBOARD_URL = (
	import.meta.env.VITE_DASHBOARD_URL || 'https://dashboard.tagadapay.io'
).replace(/\/+$/, '')

const DASHBOARD_APPLY_URL = `${DASHBOARD_URL}/merchant/requests/new`

/** Where the merchant follows an application they already submitted. */
function dashboardApplicationUrl(tpaId: string | null): string {
	return tpaId
		? `${DASHBOARD_URL}/merchant/tpa/${encodeURIComponent(tpaId)}`
		: `${DASHBOARD_URL}/merchant/requests`
}

/** States where the merchant has a live application and must not be re-asked to apply. */
const PENDING_STATES: ProcessingApplicationState[] = ['in_review', 'approved', 'live']

type View = 'choice' | 'processor'

/* -------------------------------------------------------------------------- */
/*  Existing TagadaPay application                                            */
/* -------------------------------------------------------------------------- */

const APPLICATION_BADGE: Record<
	Exclude<ProcessingApplicationState, 'none'>,
	'green' | 'blue' | 'red' | 'gray'
> = {
	draft: 'gray',
	in_review: 'blue',
	approved: 'blue',
	live: 'green',
	rejected: 'red',
}

interface ApplicationSummary {
	state: ProcessingApplicationState
	tpaId: string | null
	businessName: string | null
}

/**
 * Standing reminder that the merchant HAS an application, shown above the card
 * form. Without it, an approved merchant reads the card step as "they want me
 * to start over with another processor" and bounces back to the choice screen.
 */
function ApplicationStatusStrip({ application }: { application: ApplicationSummary }) {
	const { t } = useTranslation('common')
	if (application.state === 'none') return null
	const state = application.state

	return (
		<div className="bg-muted/40 rounded-lg border p-4">
			<div className="flex items-start gap-3">
				<div className="bg-primary/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
					<FileText className="text-primary h-4 w-4" />
				</div>
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<p className="text-foreground text-sm font-semibold">
							{t('activateAccount.applicationHeading')}
						</p>
						<Badge variant={APPLICATION_BADGE[state]}>
							{t(`activateAccount.state.${state}.label`)}
						</Badge>
					</div>
					<p className="text-muted-foreground mt-1 text-xs leading-relaxed">
						{t(`activateAccount.state.${state}.body`)}
					</p>
					<a
						href={dashboardApplicationUrl(application.tpaId)}
						target="_blank"
						rel="noopener noreferrer"
						className="text-primary mt-2 inline-flex items-center gap-1.5 text-xs font-medium hover:underline"
					>
						{t('activateAccount.applicationView')}
						<ArrowRight className="h-3 w-3" />
					</a>
				</div>
			</div>
		</div>
	)
}

/* -------------------------------------------------------------------------- */
/*  Choice screen                                                             */
/* -------------------------------------------------------------------------- */

function ChoiceScreen({
	onPickProcessor,
	application,
}: {
	onPickProcessor: () => void
	application: ApplicationSummary
}) {
	const { t } = useTranslation('common')
	const hasApplication = application.state !== 'none'
	const state = application.state
	const goToDashboard = () => {
		window.location.href = withDashboardCometToken(
			hasApplication ? dashboardApplicationUrl(application.tpaId) : DASHBOARD_APPLY_URL,
		)
	}

	return (
		<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
			{/* Option A — already have a processor */}
			<Card className="flex flex-col justify-between transition-shadow hover:shadow-md">
				<CardHeader>
					<div className="bg-primary/10 mb-2 flex h-11 w-11 items-center justify-center rounded-xl">
						<Plug className="text-primary h-5 w-5" />
					</div>
					<CardTitle className="text-xl">
						I already have a payment processor
					</CardTitle>
					<CardDescription className="text-sm leading-relaxed">
						Plug in Stripe, Braintree, Checkout.com or any of the 30+
						processors you already use. Add a billing card to activate and
						start processing right away.
					</CardDescription>
				</CardHeader>
				<CardContent className="flex-1">
					<ProcessorStrip compact />
				</CardContent>
				<CardFooter className="flex-col items-stretch gap-2">
					<Button onClick={onPickProcessor} className="w-full" size="lg">
						<span className="flex items-center gap-2">
							Connect my processor
							<ArrowRight className="h-4 w-4" />
						</span>
					</Button>
					<p className="text-muted-foreground text-center text-xs">
						A billing card is required to unlock app.tagada.io.
					</p>
				</CardFooter>
			</Card>

			{/* Option B — need payment processing */}
			<Card className="border-primary/40 bg-primary/[0.03] dark:bg-primary/10 relative flex flex-col justify-between border-2 transition-shadow hover:shadow-md">
				{!hasApplication && (
					<div className="absolute -top-3 left-6">
						<Badge variant="green">Recommended</Badge>
					</div>
				)}
				<CardHeader>
					<div className="bg-primary/10 dark:bg-primary/20 mb-2 flex h-11 w-11 items-center justify-center rounded-xl">
						{hasApplication ? (
							<Clock className="text-primary h-5 w-5" />
						) : (
							<Sparkles className="text-primary h-5 w-5" />
						)}
					</div>
					<CardTitle className="text-xl">
						{hasApplication
							? t('activateAccount.applicationCardTitle')
							: 'I need payment processing'}
					</CardTitle>
					<CardDescription className="text-sm leading-relaxed">
						{state !== 'none'
							? t(`activateAccount.state.${state}.body`)
							: 'Get approved for TagadaPay payments and let us handle the rest — simple, transparent pricing with no setup fees.'}
					</CardDescription>
				</CardHeader>
				<CardContent className="flex-1">
					{state !== 'none' ? (
						<div className="flex flex-wrap items-center gap-2">
							<Badge variant={APPLICATION_BADGE[state]}>
								{t(`activateAccount.state.${state}.label`)}
							</Badge>
							{application.businessName && (
								<span className="text-muted-foreground text-sm">
									{application.businessName}
								</span>
							)}
						</div>
					) : (
						<>
							<div className="mb-4 flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
								<span className="text-muted-foreground text-sm">Starting at</span>
								<span className="text-foreground text-3xl font-bold">2.9%</span>
								<span className="text-muted-foreground text-sm">+ 29¢</span>
								<span className="text-muted-foreground text-sm">
									per transaction
								</span>
							</div>
							<ul className="space-y-2 text-sm">
								{[
									'No setup fees, no monthly minimums',
									'Fast online application — approved in days',
									'Built-in fraud protection & chargeback tools',
								].map((item) => (
									<li
										key={item}
										className="text-muted-foreground flex items-start gap-2"
									>
										<Check className="text-primary mt-0.5 h-4 w-4 shrink-0" />
										{item}
									</li>
								))}
							</ul>
						</>
					)}
				</CardContent>
				<CardFooter>
					<Button
						onClick={goToDashboard}
						className="w-full"
						size="lg"
						variant={hasApplication ? 'outline' : 'default'}
					>
						<span className="flex items-center gap-2">
							{!hasApplication
								? 'Apply for TagadaPay'
								: state === 'draft'
									? t('activateAccount.applicationFinish')
									: t('activateAccount.applicationView')}
							<ArrowRight className="h-4 w-4" />
						</span>
					</Button>
				</CardFooter>
			</Card>
		</div>
	)
}

/* -------------------------------------------------------------------------- */
/*  Processor logos strip                                                     */
/* -------------------------------------------------------------------------- */

function ProcessorStrip({ compact }: { compact?: boolean }) {
	const logos = compact
		? ONBOARDING_PROCESSOR_LOGOS.slice(0, 12)
		: ONBOARDING_PROCESSOR_LOGOS

	return (
		<div className="flex flex-wrap items-center gap-x-4 gap-y-3">
			{logos.map((p) => (
				<img
					key={p.name}
					src={p.logo}
					alt={p.name}
					title={p.name}
					loading="lazy"
					className="h-5 w-auto max-w-[68px] object-contain opacity-60 grayscale transition hover:opacity-100 hover:grayscale-0"
				/>
			))}
			{compact && (
				<span className="text-muted-foreground text-xs font-medium">
					+ {ONBOARDING_PROCESSOR_LOGOS.length - 12} more
				</span>
			)}
		</div>
	)
}

/* -------------------------------------------------------------------------- */
/*  Processor / card-setup screen (Option A)                                  */
/* -------------------------------------------------------------------------- */

function ProcessorScreen({
	onBack,
	application,
	initialError,
}: {
	onBack: () => void
	application: ApplicationSummary
	initialError?: string | null
}) {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const { urlSlug } = useOrgSlug()
	const { refetch } = usePaymentStatus()
	const createSetupSession = useCreateBillingSetupSession()
	const completeSetup = useCompleteBillingSetup()

	const [session, setSession] = useState<BillingSetupSession | null>(null)
	const [isFinalizing, setIsFinalizing] = useState(false)
	const [cardError, setCardError] = useState<string | null>(initialError ?? null)

	// Start the card setup on the provider that bills this account as soon as
	// the merchant opens this step. The session is tagged by provider so the
	// matching form renders below.
	useEffect(() => {
		let cancelled = false
		createSetupSession.mutate(
			{},
			{
				onSuccess: (data) => {
					if (cancelled) return
					setSession(data)
				},
				onError: (error) => {
					if (cancelled) return
					toast.error(
						error.message || 'Could not start card setup. Please try again.',
					)
				},
			},
		)
		return () => {
			cancelled = true
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleConfirmed = (token: string) => {
		if (!session) return
		setIsFinalizing(true)
		setCardError(null)
		completeSetup.mutate(
			{
				provider: session.provider,
				token,
				// Vault-billed accounts may go through the bank's 3-D Secure
				// page; the server sends the browser back to this page.
				returnUrl: currentPageReturnUrl(),
			},
			{
				onSuccess: async (result) => {
					if (result.status === 'requires_action') {
						window.location.assign(result.redirectUrl)
						return
					}
					await refetch()
					toast.success('Your account is activated. Welcome aboard!')
					if (urlSlug) {
						// Continue any in-progress onboarding (e.g. plugin install /
						// store creation) rather than dropping the merchant on home.
						const destination = resolvePostActivationDestination(urlSlug)
						navigate(destination, { replace: true })
					}
				},
				onError: (error) => {
					setIsFinalizing(false)
					const message = error.message || 'Could not save your card. Please try again.'
					setCardError(message)
					toast.error(message)
				},
			},
		)
	}

	const isPreparing = createSetupSession.isPending || !session

	return (
		<div className="mx-auto w-full max-w-xl space-y-6">
			<button
				type="button"
				onClick={onBack}
				className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm transition"
			>
				<ArrowLeft className="h-4 w-4" />
				{application.state === 'none'
					? 'Back'
					: t('activateAccount.applicationBack')}
			</button>

			<div className="space-y-1">
				<h2 className="text-foreground text-2xl font-bold tracking-tight">
					Activate your account
				</h2>
				<p className="text-muted-foreground text-sm leading-relaxed">
					Add a billing card to unlock your account. You only pay as you process
					real transactions — there's no charge today.
				</p>
			</div>

			{application.state !== 'none' && (
				<ApplicationStatusStrip application={application} />
			)}

			<Card>
				<CardContent className="space-y-5 p-6">
					<div>
						<p className="text-muted-foreground mb-2 text-xs font-medium uppercase tracking-wide">
							Works with the processors you already use
						</p>
						<ProcessorStrip />
					</div>

					<div className="border-border border-t pt-5">
						{createSetupSession.isError ? (
							<div className="space-y-3 text-center">
								<p className="text-muted-foreground text-sm">
									We couldn't start card setup.
								</p>
								<Button
									variant="outline"
									onClick={() => createSetupSession.mutate({}, {
										onSuccess: (data) => setSession(data),
									})}
								>
									Try again
								</Button>
							</div>
						) : isPreparing || !session ? (
							<div className="flex items-center justify-center gap-2 py-10">
								<Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
								<span className="text-muted-foreground text-sm">
									Preparing secure card form…
								</span>
							</div>
						) : session.provider === 'stripe' ? (
							<EmbeddedCardSetup
								clientSecret={session.clientSecret}
								publishableKey={session.publishableKey}
								onConfirmed={handleConfirmed}
								isFinalizing={isFinalizing}
							/>
						) : (
							// Vault-billed account (Checkout.com): the card goes into our
							// vault, then the server verifies it on the acquirer.
							<VaultCardSetup
								publicKey={session.vault.publicKey}
								tokenPurpose={session.vault.tokenPurpose}
								onTokenized={handleConfirmed}
								isFinalizing={isFinalizing}
								error={cardError}
							/>
						)}
					</div>
				</CardContent>
			</Card>

			{application.state !== 'none' && (
				<p className="text-muted-foreground text-center text-xs leading-relaxed">
					{t('activateAccount.apiOnlyNote')}
				</p>
			)}

			<div className="text-muted-foreground flex items-center justify-center gap-4 text-xs">
				<span className="flex items-center gap-1.5">
					<ShieldCheck className="h-3.5 w-3.5" /> PCI-compliant
				</span>
				<span className="flex items-center gap-1.5">
					<CreditCard className="h-3.5 w-3.5" /> Powered by Stripe
				</span>
			</div>
		</div>
	)
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Account activation gate. Until the account has a valid billing payment
 * method the merchant cannot reach the CRM. Two paths only:
 *
 *  A. "I already have a processor" → plug in an existing processor; add a
 *     billing card via embedded Stripe Elements to unlock the CRM.
 *  B. "I need payment processing" → redirect to the TagadaPay dashboard to
 *     apply for Merchant-of-Record processing.
 */
export default function ActivateAccountPage() {
	const { urlSlug } = useOrgSlug()
	const { canBypass, isLoaded: claimsLoaded } = usePrivilegedStaffBypass()
	const { hasPaymentMethod, isLoading: isLoadingPayment } = usePaymentStatus()
	const { t } = useTranslation('common')
	const {
		application,
		state: applicationState,
		isLoading: isLoadingApplication,
	} = useProcessingApplication()
	// Back from the bank's 3-D Secure page. A verified card needs nothing
	// here: the payment status flips and the redirect below takes over.
	const [cardSetupReturn] = useState(readCardSetupReturn)
	const cardSetupError =
		cardSetupReturn && cardSetupReturn !== 'verified' ? CARD_SETUP_RETURN_ERRORS[cardSetupReturn] : null
	// null = "the merchant hasn't chosen", so the default below still applies.
	// A failed card verification reopens the card step with the reason.
	const [picked, setPicked] = useState<View | null>(cardSetupError ? 'processor' : null)

	const summary: ApplicationSummary = {
		state: applicationState,
		tpaId: application?.tpaId ?? null,
		businessName: application?.businessName ?? null,
	}

	// A merchant who already applied lands straight on the card step: the
	// choice screen would offer them the application they already submitted,
	// which is exactly how they end up looping between the two products.
	const view: View =
		picked ?? (PENDING_STATES.includes(applicationState) ? 'processor' : 'choice')

	if (!claimsLoaded || isLoadingPayment || isLoadingApplication) {
		return (
			<div className="flex h-screen items-center justify-center">
				<Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
			</div>
		)
	}

	if (!isActivateAccountFunnelEnabled() && urlSlug) {
		return <Navigate to={ORG_ROUTES(urlSlug).BASE()} replace />
	}

	if (canBypass && urlSlug) {
		return <Navigate to={ORG_ROUTES(urlSlug).BASE()} replace />
	}

	if (hasPaymentMethod && urlSlug) {
		// Keep this redirect in lockstep with ProcessorScreen.handleConfirmed:
		// a card setup refetches payment status, which can re-render this page
		// and fire the redirect before the explicit navigate runs. Both must
		// resolve to the same destination, otherwise mid-onboarding merchants
		// race to /home instead of continuing to store creation.
		const destination = resolvePostActivationDestination(urlSlug)
		return <Navigate to={destination} replace />
	}

	return (
		<div className="bg-background flex h-dvh flex-col overflow-y-auto">
			<BlockingPageAccountBar />
			<div className="flex flex-1 items-center justify-center px-4 py-8 sm:py-12">
				<div className="w-full max-w-4xl space-y-8 sm:space-y-10">
					<div className="text-center">
						<h1 className="text-foreground text-2xl font-bold tracking-tight sm:text-3xl">
							Activate your account
						</h1>
						<p className="text-muted-foreground mx-auto mt-3 max-w-xl text-sm leading-relaxed sm:text-base">
							{view === 'choice'
								? 'How would you like to get started with TagadaPay?'
								: summary.state === 'none'
									? 'One last step to unlock everything.'
									: t('activateAccount.applicationSubtitle')}
						</p>
					</div>

					{view === 'choice' ? (
						<ChoiceScreen
							onPickProcessor={() => setPicked('processor')}
							application={summary}
						/>
					) : (
						<ProcessorScreen
							onBack={() => setPicked('choice')}
							application={summary}
							initialError={cardSetupError}
						/>
					)}

					{view === 'choice' && brandingConfig.supportEmail && (
						<p className="text-muted-foreground text-center text-sm">
							Not sure which to pick? Reach out at{' '}
							<a
								href={`mailto:${brandingConfig.supportEmail}`}
								className="text-primary hover:underline"
							>
								{brandingConfig.supportEmail}
							</a>
						</p>
					)}
				</div>
			</div>
		</div>
	)
}
